import {
  WebGLRenderer, Scene, PCFSoftShadowMap,
  PerspectiveCamera, Group, Mesh, Color,
  PlaneGeometry, BoxGeometry, CylinderGeometry, SphereGeometry,
  MeshLambertMaterial, MeshPhongMaterial, MeshStandardMaterial,
  HemisphereLight, SpotLight, PointLight,
  FogExp2,
} from 'three';
import { FollowCamera } from '../camera/FollowCamera';
import { InputManager } from '../utils/InputManager';
import { AudioManager } from '../audio/AudioManager';
import { HangarBGM } from '../audio/HangarBGM';
import { Game } from './Game';
import { StageData } from './StageLoader';
import { ALL_STAGES } from '../data/stages/index';
import { ScoreResult, Grade } from './ScoreCalculator';
import { AircraftData, AIRCRAFT_LIST } from '../data/aircraft';
import { buildStealthModel } from '../aircraft/PlayerAircraft';
import { buildGen4Model } from '../aircraft/Gen4Fighter';
import { buildMarineModel } from '../aircraft/MarineFighter';

type FlowState = 'title' | 'aircraft-select' | 'stage-select' | 'playing' | 'result';

const GRADE_COLORS: Record<Grade, string> = {
  S: '#ffd700',
  A: '#00ff88',
  B: '#00bbff',
  C: '#cccccc',
  D: '#888888',
};

export class GameFlow {
  private renderer: WebGLRenderer;
  private input: InputManager;
  private followCam: FollowCamera;
  private audio: AudioManager;

  private state: FlowState = 'title';
  private currentGame: Game | null = null;
  private currentScene: Scene | null = null;
  private currentStageIndex = 0;
  private selectedAircraft: AircraftData = AIRCRAFT_LIST[0];

  // ハンガー3Dビュー
  private hangarScene: Scene | null = null;
  private hangarCamera: PerspectiveCamera | null = null;
  private hangarAircraft: Group | null = null;
  private hangarRotation = 0;
  private hangarAircraftIndex = 0;
  private hangarBGM: HangarBGM;

  // グローバルミュートボタン
  private muteBtn: HTMLElement;

  // HTML要素
  private titleScreen: HTMLElement;
  private aircraftSelect: HTMLElement;
  private stageSelect: HTMLElement;
  private stageList: HTMLElement;
  private resultOverlay: HTMLElement;
  private hud: HTMLElement;

  constructor(canvas: HTMLCanvasElement) {
    this.renderer = new WebGLRenderer({ canvas, antialias: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = PCFSoftShadowMap;

    this.input = new InputManager(canvas);
    this.followCam = new FollowCamera(window.innerWidth / window.innerHeight);
    this.audio = new AudioManager();
    this.hangarBGM = new HangarBGM(this.audio);

    this.muteBtn = document.getElementById('global-mute')!;
    this.titleScreen = document.getElementById('title-screen')!;
    this.aircraftSelect = document.getElementById('aircraft-select')!;
    this.stageSelect = document.getElementById('stage-select')!;
    this.stageList = document.getElementById('stage-list')!;
    this.resultOverlay = document.getElementById('result-overlay')!;
    this.hud = document.getElementById('hud')!;

    this.setupGlobalMute();
    this.setupTitle();
    this.setupAircraftSelect();
    this.setupStageSelect();
    this.setupResultScreen();
    this.setupResize();

    this.showTitle();
  }

  // ═══════════════════════════════════════════════
  // グローバルミュート
  // ═══════════════════════════════════════════════

  private setupGlobalMute(): void {
    // モバイルはデフォルトミュート
    if (this.input.isMobile) {
      this.audio.master.gain.value = 0;
      // AudioManager内部のmuted状態を合わせる
      if (!this.audio.isMuted) this.audio.toggleMute();
      this.updateMuteIcon(true);
    }

    // クリック / タッチでミュートトグル
    const toggle = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      const muted = this.audio.toggleMute();
      this.updateMuteIcon(muted);
    };
    this.muteBtn.addEventListener('click', toggle);
    this.muteBtn.addEventListener('touchstart', toggle, { passive: false });

    // キーボード M キーでミュートトグル（全画面共通）
    document.addEventListener('keydown', (e) => {
      if (e.code === 'KeyM' && !e.repeat) {
        const muted = this.audio.toggleMute();
        this.updateMuteIcon(muted);
      }
    });
  }

  private updateMuteIcon(muted: boolean): void {
    this.muteBtn.textContent = muted ? '🔇 MUTE' : '🔊';
    this.muteBtn.style.color = muted ? '#ff8844' : '#00ff88';
    this.muteBtn.style.textShadow = muted ? '0 0 6px #ff8844' : '0 0 6px #00ff88';
  }

  private setupResize(): void {
    window.addEventListener('resize', () => {
      this.renderer.setSize(window.innerWidth, window.innerHeight);
      const aspect = window.innerWidth / window.innerHeight;
      this.followCam.onResize(aspect);
      if (this.hangarCamera) {
        this.hangarCamera.aspect = aspect;
        this.hangarCamera.updateProjectionMatrix();
      }
    });
  }

  // ═══════════════════════════════════════════════
  // タイトル画面
  // ═══════════════════════════════════════════════

  private showTitle(): void {
    this.hideAll();
    this.state = 'title';
    this.titleScreen.style.display = 'flex';
  }

  private setupTitle(): void {
    document.getElementById('title-start-btn')!.addEventListener('click', () => {
      this.showAircraftSelect();
    });
    // モバイルタッチ対応
    document.getElementById('title-start-btn')!.addEventListener('touchstart', (e) => {
      e.preventDefault();
      this.showAircraftSelect();
    }, { passive: false });
  }

  // ═══════════════════════════════════════════════
  // 機体選択画面（3Dハンガービュー）
  // ═══════════════════════════════════════════════

  private showAircraftSelect(): void {
    this.hideAll();
    this.state = 'aircraft-select';
    this.buildHangarScene();
    this.setHangarAircraft(this.hangarAircraftIndex);
    this.aircraftSelect.style.display = 'flex';

    // BGM開始
    this.audio.resume();
    this.audio.resumeAudio();
    this.hangarBGM.start();
  }

  private buildHangarScene(): void {
    const s = new Scene();
    this.hangarScene = s;
    s.background = new Color(0x111115);
    s.fog = new FogExp2(0x111115, 0.012);

    // カメラ
    const aspect = window.innerWidth / window.innerHeight;
    this.hangarCamera = new PerspectiveCamera(50, aspect, 0.5, 500);
    this.hangarCamera.position.set(22, 12, 22);
    this.hangarCamera.lookAt(0, 2, 0);

    // ─── 照明 ───
    const hemi = new HemisphereLight(0xccccdd, 0x333344, 0.8);
    s.add(hemi);

    // メインスポットライト（機体真上）
    const spot1 = new SpotLight(0xffffff, 250, 100, Math.PI / 5, 0.4);
    spot1.position.set(5, 28, 5);
    spot1.target.position.set(0, 0, 0);
    s.add(spot1); s.add(spot1.target);

    // サブスポットライト（斜め後方、少し青み）
    const spot2 = new SpotLight(0xaaccff, 140, 100, Math.PI / 4, 0.5);
    spot2.position.set(-12, 22, -10);
    spot2.target.position.set(0, 2, 0);
    s.add(spot2); s.add(spot2.target);

    // フィルライト（暗部を持ち上げる）
    const spot3 = new SpotLight(0xffeedd, 90, 80, Math.PI / 3, 0.6);
    spot3.position.set(15, 18, 15);
    spot3.target.position.set(0, 0, 0);
    s.add(spot3); s.add(spot3.target);

    // ─── マテリアル ───
    const matConc = new MeshStandardMaterial({ color: 0x3a3a3c, roughness: 0.9, metalness: 0.05 });
    const matWall = new MeshStandardMaterial({ color: 0x2c2c30, roughness: 0.85, metalness: 0.1 });
    const matSteel = new MeshStandardMaterial({ color: 0x5a5a60, roughness: 0.4, metalness: 0.7 });
    const matDarkSteel = new MeshStandardMaterial({ color: 0x3a3a40, roughness: 0.5, metalness: 0.6 });
    const matYellow = new MeshPhongMaterial({ color: 0xffcc00, emissive: 0x332200 });
    const matRed = new MeshPhongMaterial({ color: 0xcc2222, emissive: 0x220000 });
    const matOrange = new MeshPhongMaterial({ color: 0xff6600, emissive: 0x331100 });
    const matGreen = new MeshStandardMaterial({ color: 0x2a5a2a, roughness: 0.7, metalness: 0.2 });
    const matBlue = new MeshStandardMaterial({ color: 0x2244aa, roughness: 0.6, metalness: 0.3 });

    // ─── 床（コンクリート） ───
    const floor = new Mesh(new PlaneGeometry(80, 80), matConc);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -0.5;
    s.add(floor);

    // ─── 壁 ───
    const backWall = new Mesh(new BoxGeometry(80, 32, 1), matWall);
    backWall.position.set(0, 15.5, -40);
    s.add(backWall);
    const leftWall = new Mesh(new BoxGeometry(1, 32, 80), matWall);
    leftWall.position.set(-40, 15.5, 0);
    s.add(leftWall);
    const rightWall = new Mesh(new BoxGeometry(1, 32, 80), matWall);
    rightWall.position.set(40, 15.5, 0);
    s.add(rightWall);

    // ─── 天井（アーチ） ───
    const matRoof = new MeshStandardMaterial({ color: 0x222228, roughness: 0.8, metalness: 0.2 });
    const roof = new Mesh(new CylinderGeometry(42, 42, 82, 16, 1, false, 0, Math.PI), matRoof);
    roof.rotation.z = Math.PI / 2;
    roof.rotation.y = Math.PI / 2;
    roof.position.set(0, 30, 0);
    s.add(roof);

    // ─── ハンガードア（前面開口部） ───
    // 左右のドアレール
    for (const sx of [-1, 1]) {
      const rail = new Mesh(new BoxGeometry(2, 30, 1), matDarkSteel);
      rail.position.set(sx * 39, 14.5, 40);
      s.add(rail);
    }
    // ドア上部ビーム
    const doorBeam = new Mesh(new BoxGeometry(80, 2, 1.5), matSteel);
    doorBeam.position.set(0, 29.5, 40);
    s.add(doorBeam);
    // 半開きドアパネル（左右端に）
    for (const sx of [-1, 1]) {
      const panel = new Mesh(new BoxGeometry(10, 28, 0.5), matDarkSteel);
      panel.position.set(sx * 35, 13.5, 40);
      s.add(panel);
    }

    // ─── 鉄骨トラス（天井梁） ───
    for (const zOff of [-24, -8, 8, 24]) {
      // メインビーム（横方向）
      const beam = new Mesh(new BoxGeometry(78, 1.2, 0.8), matSteel);
      beam.position.set(0, 28, zOff);
      s.add(beam);
      // 斜めブレース（左右）
      for (const sx of [-1, 1]) {
        const brace = new Mesh(new BoxGeometry(0.4, 8, 0.4), matSteel);
        brace.position.set(sx * 30, 25, zOff);
        brace.rotation.z = sx * 0.3;
        s.add(brace);
      }
    }

    // ─── 支柱（左右壁際） ───
    for (const sx of [-1, 1]) {
      for (const zOff of [-30, -15, 0, 15, 30]) {
        // H鋼風の柱
        const pillar = new Mesh(new BoxGeometry(1.5, 30, 1.5), matSteel);
        pillar.position.set(sx * 39, 14.5, zOff);
        s.add(pillar);
        // フランジ
        const flange = new Mesh(new BoxGeometry(2.5, 30, 0.3), matSteel);
        flange.position.set(sx * 39, 14.5, zOff);
        s.add(flange);
      }
    }

    // ─── 蛍光灯ユニット（天井吊り下げ） ───
    for (const zOff of [-16, 0, 16]) {
      for (const xOff of [-12, 12]) {
        // 灯具本体
        const fixture = new Mesh(new BoxGeometry(1.2, 0.3, 4), matSteel);
        fixture.position.set(xOff, 26.5, zOff);
        s.add(fixture);
        // 発光部
        const tube = new Mesh(
          new BoxGeometry(0.3, 0.15, 3.5),
          new MeshPhongMaterial({ color: 0xffffee, emissive: 0xeeeedd, emissiveIntensity: 0.6 }),
        );
        tube.position.set(xOff, 26.3, zOff);
        s.add(tube);
        // ポイントライト
        const pl = new PointLight(0xffeedd, 25, 40);
        pl.position.set(xOff, 26, zOff);
        s.add(pl);
      }
    }

    // ─── 床マーキング ───
    // 黄色誘導線
    for (const sx of [-1, 1]) {
      const line = new Mesh(new BoxGeometry(0.15, 0.02, 35), matYellow);
      line.position.set(sx * 8, -0.48, -2);
      s.add(line);
    }
    // 横線（機体停止位置）
    const stopLine = new Mesh(new BoxGeometry(16, 0.02, 0.15), matYellow);
    stopLine.position.set(0, -0.48, -20);
    s.add(stopLine);
    // 危険エリア（赤白ストライプ — 簡易）
    for (let i = 0; i < 8; i++) {
      const stripe = new Mesh(new BoxGeometry(1.2, 0.02, 0.4), i % 2 === 0 ? matRed : matYellow);
      stripe.position.set(-4 + i * 1.2, -0.48, 16);
      s.add(stripe);
    }

    // ─── 工具キャビネット（左壁際） ───
    for (const zOff of [-22, -10, 5]) {
      const cabinet = new Mesh(new BoxGeometry(2, 4, 3), matBlue);
      cabinet.position.set(-38, 1.5, zOff);
      s.add(cabinet);
      // 天板
      const top = new Mesh(new BoxGeometry(2.2, 0.1, 3.2), matSteel);
      top.position.set(-38, 3.55, zOff);
      s.add(top);
    }

    // ─── 木箱・ドラム缶（右壁際） ───
    // 木箱
    const matWood = new MeshStandardMaterial({ color: 0x8a6a3a, roughness: 0.9, metalness: 0 });
    for (const [x, z, w, h, d] of [
      [37, -25, 3, 2.5, 3],
      [37, -22, 2.5, 2, 2.5],
      [38, -22, 2, 1.5, 2], // 上に積む
    ] as number[][]) {
      const crate = new Mesh(new BoxGeometry(w, h, d), matWood);
      crate.position.set(x, h / 2 - 0.5 + (d < 2.5 ? 2 : 0), z);
      s.add(crate);
    }

    // ドラム缶
    const matDrum = new MeshStandardMaterial({ color: 0x446644, roughness: 0.6, metalness: 0.4 });
    for (const [x, z] of [[36, -15], [37.5, -14], [36.8, -16.5]]) {
      const drum = new Mesh(new CylinderGeometry(0.7, 0.7, 2.2, 12), matDrum);
      drum.position.set(x, 0.6, z);
      s.add(drum);
      // リム
      const rim = new Mesh(new CylinderGeometry(0.75, 0.75, 0.1, 12), matSteel);
      rim.position.set(x, 1.7, z);
      s.add(rim);
    }

    // ─── 消火器（背面壁際） ───
    for (const xOff of [-20, 20]) {
      // 本体
      const ext = new Mesh(new CylinderGeometry(0.25, 0.25, 1.2, 8), matRed);
      ext.position.set(xOff, 0.1, -38);
      s.add(ext);
      // ノズル
      const noz = new Mesh(new CylinderGeometry(0.06, 0.06, 0.3, 6), matDarkSteel);
      noz.position.set(xOff, 0.8, -38);
      s.add(noz);
    }

    // ─── 作業台（右壁際中央付近） ───
    const bench = new Mesh(new BoxGeometry(6, 0.15, 2), matSteel);
    bench.position.set(37, 2.0, 0);
    s.add(bench);
    // 脚
    for (const [dx, dz] of [[-2.5, -0.8], [2.5, -0.8], [-2.5, 0.8], [2.5, 0.8]]) {
      const leg = new Mesh(new BoxGeometry(0.3, 2.5, 0.3), matSteel);
      leg.position.set(37 + dx, 0.75, dz);
      s.add(leg);
    }

    // ─── セーフティコーン（機体周囲） ───
    for (const [cx, cz] of [[-10, 10], [10, 10], [-10, -12], [10, -12]]) {
      // コーン本体
      const cone = new Mesh(new CylinderGeometry(0.05, 0.4, 1.2, 8), matOrange);
      cone.position.set(cx, 0.1, cz);
      s.add(cone);
      // ベース
      const base = new Mesh(new BoxGeometry(0.8, 0.1, 0.8), matDarkSteel);
      base.position.set(cx, -0.45, cz);
      s.add(base);
    }

    // ─── 壁面パイプ（左壁） ───
    for (const yOff of [10, 14]) {
      const pipe = new Mesh(new CylinderGeometry(0.2, 0.2, 70, 8), matSteel);
      pipe.rotation.x = Math.PI / 2;
      pipe.position.set(-39, yOff, 0);
      s.add(pipe);
    }

    // ─── 電気パネル（背面壁） ───
    const panel = new Mesh(new BoxGeometry(3, 4, 0.3), matDarkSteel);
    panel.position.set(10, 5, -39.3);
    s.add(panel);
    // 表示灯（緑・赤の小さな球）
    for (let i = 0; i < 3; i++) {
      const light = new Mesh(
        new SphereGeometry(0.08, 8, 8),
        new MeshPhongMaterial({ color: i < 2 ? 0x00ff44 : 0xff2222, emissive: i < 2 ? 0x003300 : 0x220000 }),
      );
      light.position.set(9 + i * 1, 6, -39.1);
      s.add(light);
    }

    // ─── 外の光（ハンガードア開口部からの環境光） ───
    const envLight = new SpotLight(0x8899bb, 60, 80, Math.PI / 3, 0.8);
    envLight.position.set(0, 20, 55);
    envLight.target.position.set(0, 0, 20);
    s.add(envLight); s.add(envLight.target);
  }

  private setHangarAircraft(index: number): void {
    // 古い機体を除去
    if (this.hangarAircraft && this.hangarScene) {
      this.hangarScene.remove(this.hangarAircraft);
    }

    this.hangarAircraftIndex = index;
    const ac = AIRCRAFT_LIST[index];
    this.selectedAircraft = ac;

    // 機体モデル生成
    if (ac.id === 'gen4') {
      this.hangarAircraft = buildGen4Model();
    } else if (ac.id === 'marine') {
      this.hangarAircraft = buildMarineModel();
    } else {
      this.hangarAircraft = buildStealthModel();
    }
    this.hangarAircraft.position.set(0, 0, 0);
    this.hangarRotation = Math.PI; // 機首をカメラ側に向ける
    this.hangarScene!.add(this.hangarAircraft);

    // 情報パネル更新
    this.updateAircraftInfoPanel(ac);
  }

  private updateAircraftInfoPanel(ac: AircraftData): void {
    document.getElementById('aircraft-info-name')!.textContent = ac.nameEn;
    document.getElementById('aircraft-info-name-jp')!.textContent = ac.name;

    const statsBar = (label: string, value: number) => {
      const filled = '\u2588'.repeat(value);
      const empty = '\u2591'.repeat(5 - value);
      return `<div><span class="stat-label">${label}</span> ${filled}${empty}</div>`;
    };
    document.getElementById('aircraft-info-stats')!.innerHTML =
      statsBar('SPD', ac.stats.speed) +
      statsBar('MOB', ac.stats.mobility) +
      statsBar('DEF', ac.stats.defense) +
      statsBar('ATK', ac.stats.firepower);

    document.getElementById('aircraft-info-spec')!.textContent =
      `HP:${ac.maxHp}  MSL:${ac.missiles}  VUL:${ac.vulcanAmmo}`;
  }

  private disposeHangar(): void {
    this.hangarBGM.stop();
    this.hangarAircraft = null;
    this.hangarScene = null;
    this.hangarCamera = null;
  }

  private setupAircraftSelect(): void {
    const confirmAction = () => {
      this.disposeHangar();
      this.showStageSelect();
    };
    document.getElementById('aircraft-confirm-btn')!.addEventListener('click', confirmAction);
    document.getElementById('aircraft-confirm-btn')!.addEventListener('touchstart', (e) => {
      e.preventDefault(); confirmAction();
    }, { passive: false });

    document.getElementById('aircraft-back-btn')!.addEventListener('click', () => {
      this.disposeHangar();
      this.showTitle();
    });

    // 左右切替
    const prevAction = () => {
      const idx = (this.hangarAircraftIndex - 1 + AIRCRAFT_LIST.length) % AIRCRAFT_LIST.length;
      this.setHangarAircraft(idx);
    };
    const nextAction = () => {
      const idx = (this.hangarAircraftIndex + 1) % AIRCRAFT_LIST.length;
      this.setHangarAircraft(idx);
    };
    document.getElementById('aircraft-prev-btn')!.addEventListener('click', prevAction);
    document.getElementById('aircraft-prev-btn')!.addEventListener('touchstart', (e) => {
      e.preventDefault(); prevAction();
    }, { passive: false });
    document.getElementById('aircraft-next-btn')!.addEventListener('click', nextAction);
    document.getElementById('aircraft-next-btn')!.addEventListener('touchstart', (e) => {
      e.preventDefault(); nextAction();
    }, { passive: false });
  }

  // ═══════════════════════════════════════════════
  // ステージ選択画面
  // ═══════════════════════════════════════════════

  private showStageSelect(): void {
    this.hideAll();
    this.state = 'stage-select';
    this.populateStageList();
    this.stageSelect.style.display = 'flex';
  }

  private populateStageList(): void {
    this.stageList.innerHTML = '';
    ALL_STAGES.forEach((stage, index) => {
      const item = document.createElement('div');
      item.className = 'stage-item';
      item.innerHTML = `
        <div class="stage-item-name">${stage.name}</div>
        <div class="stage-item-desc">${stage.description}</div>
        <div class="stage-item-info">${stage.terrain.toUpperCase()} | ENEMIES: ${stage.totalEnemies}</div>
      `;
      item.addEventListener('click', () => this.startStage(index));
      item.addEventListener('touchstart', (e) => {
        e.preventDefault();
        this.startStage(index);
      }, { passive: false });
      this.stageList.appendChild(item);
    });
  }

  private setupStageSelect(): void {
    document.getElementById('stage-back-btn')!.addEventListener('click', () => {
      this.showAircraftSelect();
    });
  }

  // ═══════════════════════════════════════════════
  // ゲームプレイ
  // ═══════════════════════════════════════════════

  private startStage(index: number): void {
    this.disposeCurrentGame();
    this.hideAll();
    this.currentStageIndex = index;
    const stageData = ALL_STAGES[index];

    this.currentScene = new Scene();
    this.currentGame = new Game(
      this.currentScene,
      this.renderer,
      this.input,
      this.followCam,
      stageData,
      this.audio,
      {
        onClear: (result) => this.handleStageClear(result),
        onGameOver: () => this.handleGameOver(),
      },
      this.selectedAircraft,
    );

    this.state = 'playing';
    this.hud.style.display = '';
    document.getElementById('radar-wrap')!.style.display = '';
    document.getElementById('hud-lockdot')!.style.display = '';

    // ゲーム開始
    if (this.input.isMobile) {
      this.currentGame.start();
    } else {
      // PCはクリックでPointer Lock → Game.start()がpointerlockchangeで呼ばれる
      // 一旦start-overlayのような案内を表示
      this.showPlayingHint(stageData);
    }
  }

  private showPlayingHint(stageData: StageData): void {
    const hint = document.getElementById('playing-hint')!;
    hint.querySelector('.subtitle')!.textContent = stageData.nameEn.toUpperCase();
    hint.style.display = 'flex';
    // PCクリックでPointer Lock取得 → ゲーム開始
    const onClick = () => {
      hint.style.display = 'none';
      this.input.requestPointerLock();
      this.currentGame?.start();
      document.removeEventListener('click', onClick);
    };
    document.addEventListener('click', onClick);
  }

  // ═══════════════════════════════════════════════
  // リザルト画面
  // ═══════════════════════════════════════════════

  private handleStageClear(result: ScoreResult): void {
    this.state = 'result';
    this.showResultScreen(result, true);
  }

  private handleGameOver(): void {
    this.state = 'result';
    const failResult: ScoreResult = {
      clearTime: 0,
      remainingHpPct: 0,
      totalScore: 0,
      grade: 'D',
      isGameOver: true,
    };
    this.showResultScreen(failResult, false);
  }

  private showResultScreen(result: ScoreResult, cleared: boolean): void {
    const msg = document.getElementById('result-message')!;
    const scorePanel = document.getElementById('result-score-panel')!;
    const nextBtn = document.getElementById('result-next')!;

    if (cleared) {
      msg.textContent = 'MISSION COMPLETE';
      msg.style.color = '#00ff88';
      scorePanel.style.display = '';

      document.getElementById('result-time')!.textContent = this.formatTime(result.clearTime);
      document.getElementById('result-hp')!.textContent = `${result.remainingHpPct}%`;
      document.getElementById('result-score')!.textContent = result.totalScore.toString();
      const gradeEl = document.getElementById('result-grade')!;
      gradeEl.textContent = result.grade;
      gradeEl.style.color = GRADE_COLORS[result.grade];

      // NEXT STAGEボタン表示（次のステージがある場合のみ）
      const hasNext = this.currentStageIndex < ALL_STAGES.length - 1;
      nextBtn.style.display = hasNext ? '' : 'none';
    } else {
      msg.textContent = 'MISSION FAILED';
      msg.style.color = '#ff4444';
      scorePanel.style.display = 'none';
      nextBtn.style.display = 'none';
    }

    this.resultOverlay.style.display = 'flex';
  }

  private setupResultScreen(): void {
    document.getElementById('result-retry')!.addEventListener('click', () => {
      this.startStage(this.currentStageIndex);
    });
    document.getElementById('result-next')!.addEventListener('click', () => {
      if (this.currentStageIndex < ALL_STAGES.length - 1) {
        this.startStage(this.currentStageIndex + 1);
      }
    });
    document.getElementById('result-select')!.addEventListener('click', () => {
      this.disposeCurrentGame();
      this.showStageSelect();
    });
  }

  // ═══════════════════════════════════════════════
  // ユーティリティ
  // ═══════════════════════════════════════════════

  private hideAll(): void {
    this.titleScreen.style.display = 'none';
    this.aircraftSelect.style.display = 'none';
    this.stageSelect.style.display = 'none';
    this.resultOverlay.style.display = 'none';
    this.hud.style.display = 'none';
    document.getElementById('playing-hint')!.style.display = 'none';
    document.getElementById('pause-overlay')!.style.display = 'none';
    document.getElementById('radar-wrap')!.style.display = 'none';
    document.getElementById('hud-lockdot')!.style.display = 'none';
    const mobile = document.getElementById('mobile-controls');
    if (mobile) mobile.style.display = 'none';
  }

  private disposeCurrentGame(): void {
    if (this.currentGame) {
      this.currentGame.dispose();
      this.currentGame = null;
    }
    this.currentScene = null;
    // オーディオ停止（ミュート状態は維持）
    this.audio.pauseAudio();
  }

  private formatTime(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  update(): void {
    if (this.state === 'aircraft-select' && this.hangarScene && this.hangarCamera) {
      // 機体をゆっくり自動回転
      if (this.hangarAircraft) {
        this.hangarRotation += 0.005;
        this.hangarAircraft.rotation.y = this.hangarRotation;
      }
      this.renderer.render(this.hangarScene, this.hangarCamera);
    } else if (this.state === 'playing' && this.currentGame && this.currentScene) {
      this.currentGame.update();
      this.renderer.render(this.currentScene, this.followCam.camera);
    }
  }
}
